import {useMemo} from 'react';
import type {MbscCalendarEvent, MbscEventConnection} from '@mobiscroll/react';
import {
    generateColorVariationsForOverlappingConnections
} from "crqlar-react-common/dist/utils/hooks/reservations/calendar/calendar.util";

const CONNECTION_VARIATIONS_COUNT = 5
const DEFAULT_CONNECTION_COLOR = '#555555'

interface MbscEventConnectionsParams {
    events: MbscCalendarEvent[]
    rangeStart: Date | undefined
    rangeEnd: Date | undefined
    enabled: boolean
}

const getTimestamp = (value: unknown): number | undefined => {
    if (value === undefined || value === null || value === '') {
        return undefined
    }

    const timestamp = value instanceof Date
        ? value.getTime()
        : typeof value === 'string' || typeof value === 'number'
            ? new Date(value).getTime()
            : Number.NaN

    return Number.isFinite(timestamp) ? timestamp : undefined
}

const isEventInRange = (event: MbscCalendarEvent, rangeStart: number, rangeEnd: number): boolean => {
    const eventStart = getTimestamp(event.start)
    const eventEnd = getTimestamp(event.end) ?? eventStart

    return eventStart !== undefined &&
        eventEnd !== undefined &&
        eventEnd >= eventStart &&
        eventStart <= rangeEnd &&
        eventEnd >= rangeStart
}

/**
 * Timeline connections are view state, not query state. React Query deliberately keeps the
 * previous page's events while the next page loads, so only events belonging to the visible
 * Mobiscroll range may be used to calculate SVG connection geometry.
 */
export const buildMbscEventConnections = ({
    events,
    rangeStart,
    rangeEnd,
    enabled
}: MbscEventConnectionsParams): MbscEventConnection[] => {
    if (!enabled) {
        return []
    }

    const rangeStartTimestamp = getTimestamp(rangeStart)
    const rangeEndTimestamp = getTimestamp(rangeEnd)

    if (
        rangeStartTimestamp === undefined ||
        rangeEndTimestamp === undefined ||
        rangeEndTimestamp < rangeStartTimestamp
    ) {
        return []
    }

    const eventsByReservation = new Map<unknown, MbscCalendarEvent[]>()

    events.forEach((event) => {
        const reservationId = event.reservationId
        const eventId = event.id
        const resourceId = event.resource

        // Block periods and service events do not belong to a reservation. Missing endpoint
        // identifiers cannot produce a valid Mobiscroll connection either.
        if (
            reservationId === undefined ||
            reservationId === null ||
            eventId === undefined ||
            eventId === null ||
            resourceId === undefined ||
            resourceId === null ||
            !isEventInRange(event, rangeStartTimestamp, rangeEndTimestamp)
        ) {
            return
        }

        const reservationEvents = eventsByReservation.get(reservationId) ?? []
        reservationEvents.push(event)
        eventsByReservation.set(reservationId, reservationEvents)
    })

    const connections: MbscEventConnection[] = []
    let overlapCounter = 0

    eventsByReservation.forEach((reservationEvents) => {
        for (let i = 0; i < reservationEvents.length; i++) {
            const event = reservationEvents[i]

            for (let j = i + 1; j < reservationEvents.length; j++) {
                const otherEvent = reservationEvents[j]

                if (event.resource === otherEvent.resource || event.id === otherEvent.id) {
                    continue
                }

                overlapCounter++
                const baseColor = (event.color as string) || DEFAULT_CONNECTION_COLOR
                const colorVariations = generateColorVariationsForOverlappingConnections(
                    baseColor,
                    CONNECTION_VARIATIONS_COUNT
                )

                connections.push({
                    from: String(event.id),
                    to: String(otherEvent.id),
                    color: colorVariations.length > 0
                        ? colorVariations[overlapCounter % colorVariations.length]
                        : baseColor,
                    arrow: false,
                    type: 'ff'
                })
            }
        }
    })

    return connections
}

export const useMbscEventConnections = (params: MbscEventConnectionsParams): MbscEventConnection[] => {
    const {events, rangeStart, rangeEnd, enabled} = params

    return useMemo(
        () => buildMbscEventConnections({events, rangeStart, rangeEnd, enabled}),
        [enabled, events, rangeEnd, rangeStart]
    )
}
